import { ref } from "vue";
import { InitializationStatus } from "../constantes/constants";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";

// Core stores (always loaded)
import { useDeviceStore } from "../store/device.store";
import { useDevicePropertyStore } from "../store/deviceProperty.store";
import { useOptions } from "../store/options.store";
import { useParameterStore } from "../store/parameter.store";
import { useStartupStore } from "../store/startup.store";

// Reference data stores (preloaded for hydration)
import { useUsersStore } from "../store/users.store";
import { useCenterStore } from "../store/center.store";
import { useBuildingStore } from "../store/building.store";
import { useTypeLocationStore } from "../store/typeLocation.store";
import { useAgentStore } from "../store/agent.store";
import { useThirdPartiesStore } from "../store/thirdParties.store";
import { useCTG } from "../store/ctg.store";
import { useReservations } from "../store/reservations.store";
import { useDilutorBathStore } from "../store/dilutorBath.store";
import { useAnimals } from "../store/animals.store";
import { useBoars } from "../store/boars.store";
import { useMovementTypeStore } from "../store/movementType.store";
import { useTypeProtocolStore } from "../store/sanitaire/typeProtocol.store";
import { useSamplingEvent } from "../store/samplingEvents.store";
import { usePowderTypeStore } from "../store/powderType.store";
import { useProductionSamplingEventStore } from "../store/productionSamplingEvent.store";
import { useReformStore } from "../store/reform.store";
import { useOrderStore } from "../store/orders.store";
import { useClientOrderStore } from "../store/clientOrder.store";
import { useClientOrderRowStore } from "../store/clientOrderRow.store";
import { useStockStore } from "../store/stock.store";
import { useStockUnityStore } from "../store/stockUnity.store";
import { useStockMovementStore } from "../store/stockMovement.store";
import { useArticleStore } from "../store/article.store";
import { useBreedingStud } from "../store/breedingStuds.store";
import { usePSLotStore } from "../store/sanitaire/psLot.store";
import { useBladeStore } from "../store/blade.store";
import { useCodeStockStore } from "../store/codeStock.store";

// Device-specific stores
import { useAGPStore } from "../store/specific/agp.store";
import { usePM43CStore } from "../store/specific/pm43c.store";
import { useBalanceStore } from "../store/specific/balance.store";
import { useLabelStore } from "../store/specific/label.store";
import { usePumpStore } from "../store/specific/pump.store";
import { useRQStore } from "../store/specific/rq.store";
import { useCasaStore } from "../store/specific/casa.store";
import i18n from "@/i18n";

/**
 * Appelle window.__o2sSetStep défini dans index.html (HTML pur, avant que Vue soit monté).
 */
export function setLoadingStep(step: number) {
    if (typeof window !== 'undefined' && typeof (window as any).__o2sSetStep === 'function') {
        (window as any).__o2sSetStep(step);
    }
}

export function setLoadingStepError(step: number) {
    if (typeof window !== 'undefined' && typeof (window as any).__o2sSetStepError === 'function') {
        (window as any).__o2sSetStepError(step);
    }
}

function notifyRef(name: string) {
    if (typeof window !== 'undefined' && typeof (window as any).__o2sAddRefData === 'function') {
        (window as any).__o2sAddRefData(name);
    }
}

function notifyRefDone() {
    if (typeof window !== 'undefined' && typeof (window as any).__o2sEndRefData === 'function') {
        (window as any).__o2sEndRefData();
    }
}


export async function initializeCommonStores(): Promise<{ hasErrors: boolean }> {
    console.group("🚀 Store Initialization");

    const tGlobal0 = performance.now();
    let hasErrors = false;

    const { start, end, markComplete } = usePerformanceTracker("Initialisation");

    // Wrapper : convertit result.error=true en rejet (les services ne rejettent jamais)
    const loadChecked = async (p: Promise<any>) => {
        const r = await p;
        if (r?.error) throw new Error('API unreachable');
        return r;
    };

    const startupStore = useStartupStore();
    startupStore.currentInitStatus = InitializationStatus.MAIN_INITIALIZATION;
    console.debug("Starting main initialization");

    // Phase 1 : param + option en PARALLÈLE (indépendants l'un de l'autre)
    setLoadingStep(1);;
    const parameterStore = useParameterStore();
    const optionStore = useOptions();
    const tPhase1_0 = performance.now();
    start('Phase 1 — Paramètres + Options');
    try {
        await Promise.all([
            loadChecked(parameterStore.load({ page: 1, pageSize: 0 })).then(() =>
                console.debug('✅ ParameterStore initialized with', parameterStore.datas.length, 'parameters')
            ),
            loadChecked(optionStore.load({ page: 1, pageSize: 0 })).then(() =>
                console.debug('✅ OptionStore initialized with', optionStore.datas.length, 'options')
            ),
        ]);
        end('Phase 1 — Paramètres + Options', 'api');
        console.debug(`✅ Phase 1 (param+option) done in ${(performance.now() - tPhase1_0).toFixed(2)} ms`);
    } catch (e) {
        console.error('❌ Phase 1 (param+option) failed', e);
        setLoadingStepError(1);
        hasErrors = true;
    }

    // Phase 2 : userStore — doit être après optionStore (FAV_CENTER → currentCenter)
    setLoadingStep(2);;
    const userStore = useUsersStore();
    const tUser0 = performance.now();
    start('Phase 2 — Utilisateur');
    try {
        await userStore.loadUser();
        end('Phase 2 — Utilisateur', 'api');
        console.debug('✅ UserStore initialized, currentCenter:', userStore.currentCenter?.id, 'in', (performance.now() - tUser0).toFixed(2), 'ms');

        // Afficher le prénom + nom dans l'étape "Profil utilisateur"
        const firstName = userStore.currentUser?.givenName ?? '';
        const lastName = userStore.currentUser?.familyName ?? '';
        const userName = [firstName, lastName].filter(Boolean).join(' ');
        if (userName && typeof (window as any).__o2sSetUserName === 'function') {
            (window as any).__o2sSetUserName(userName);
        }

        // Afficher le logo de la société du centre dans l'écran de démarrage
        const societyCode = userStore.currentCenter?.society?.code3;
        if (societyCode && typeof (window as any).__o2sSetCenterIcon === 'function') {
            const logoExt = societyCode === 'GD' ? '.svg' : '.png';
            (window as any).__o2sSetCenterIcon(`images/societies/${societyCode}${logoExt}`);
        }
    } catch (e) {
        console.error('❌ Phase 2 (user) failed', e);
        setLoadingStepError(2);
        hasErrors = true;
    }

    const centerId = userStore.currentCenter?.id;

    // centerStore already populated by userStore.loadCurrentUserCenters() — no reload needed
    const centerStore = useCenterStore();
    console.debug('✅ CenterStore already loaded with', centerStore.datas.length, 'centers (via userStore)');

    // Phase 3 : TOUT en parallèle — device stores + référentiels + dépendances de hydrateBoars()
    setLoadingStep(3);
    //
    // Stores production (production, manufactoringProd, lot, productionAnalyse, index, dispatch)
    // en attente — chargement différé à la navigation (trop lourds au démarrage).
    //
    // Les stores breedingStuds/psLot sont pré-chargés ici pour éviter
    // que boarsStore.hydrateBoars() les charge séquentiellement en interne.
    // Reform/karyotype sont désormais inlinés dans la réponse GET /Boar (plus de stores séparés).
    // Possibles races (dilutorBath↔powderType) → corrigées en Phase 4.
    const tPhase3_0 = performance.now();

    const deviceStore = useDeviceStore();
    const devicePropertyStore = useDevicePropertyStore();
    const boarsStore = useBoars();
    const buildingStore = useBuildingStore();
    const typeLocationStore = useTypeLocationStore();
    const agentStore = useAgentStore();
    const thirdPartiesStore = useThirdPartiesStore();
    const ctgStore = useCTG();
    const reservationsStore = useReservations();
    const dilutorBathStore = useDilutorBathStore();
    const movementTypeStore = useMovementTypeStore();
    const typeProtocolStore = useTypeProtocolStore();
    const samplingEventStore = useSamplingEvent();
    const powderTypeStore = usePowderTypeStore();
    const breedingStudsStore = useBreedingStud();
    const psLotStore = usePSLotStore();
    const bladeStore = useBladeStore();
    const codeStockStore = useCodeStockStore();
    useProductionSamplingEventStore(); // Enregistre le handler SignalR MajProductionSamplingEvent
    useReformStore(); // Enregistre le handler SignalR MajReform
    useOrderStore(); // Enregistre le handler SignalR MajOrder
    useClientOrderStore(); // Enregistre le handler SignalR MajClientOrder
    useClientOrderRowStore(); // Enregistre le handler SignalR MajClientOrderRow
    useStockStore(); // Enregistre le handler SignalR MajStock
    useStockUnityStore(); // Enregistre le handler SignalR MajStockUnity
    useStockMovementStore(); // Enregistre le handler SignalR MajStockMovement
    useArticleStore(); // Enregistre le handler SignalR MajArticle

    // Démarrer tous les timers individuels avant le Promise.all (ils sont tous lancés en parallèle)
    start('Phase 3 — Référentiels parallèles');
    start('Devices'); start('DeviceProperties');
    start('Verrats'); start('Bâtiments'); start('Types emplacements');
    start('Agents'); start('Tiers'); start('CTG');
    start('Réservations'); start('Causes réservations'); start('Bains dilueurs');
    start('Types mouvements'); start('Types protocoles'); start('Événements prélèvement');
    start('Types poudre'); start('Réformes'); start('Caryotypes');
    start('Élevages'); start('Lots PS'); start('Lames'); start('Codes stock');
    try {
        await Promise.all([
            // Device stores (indépendants)
            loadChecked(deviceStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Devices', 'api');
                console.debug('✅ DeviceStore initialized with', deviceStore.datas.length, 'devices');
            }),
            loadChecked(devicePropertyStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('DeviceProperties', 'api');
                console.debug('✅ DevicePropertyStore initialized with', devicePropertyStore.datas.length, 'properties');
            }),

            // Référentiels
            loadChecked(boarsStore.load({ page: 1, pageSize: 0, idCenter: centerId, isPresent: true })).then(() => {
                end('Verrats', 'api');
                console.debug('✅ BoarsStore initialized with', boarsStore.datas.length, 'boars');
                notifyRef('Verrats');
            }),
            loadChecked(buildingStore.load({ page: 1, pageSize: 0, centerId: centerId })).then(() => {
                end('Bâtiments', 'api');
                console.debug('✅ BuildingStore initialized with', buildingStore.datas.length, 'buildings');
                notifyRef(i18n.global.t('startup.buildings'));
            }),
            loadChecked(typeLocationStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Types emplacements', 'api');
                console.debug('✅ TypeLocationStore initialized with', typeLocationStore.datas.length, 'type locations');
                notifyRef('Types emplacements');
            }),
            loadChecked(agentStore.load({ page: 1, pageSize: 0, centerId: centerId })).then(() => {
                end('Agents', 'api');
                console.debug('✅ AgentStore initialized with', agentStore.datas.length, 'agents');
                notifyRef('Agents');
            }),
            loadChecked(thirdPartiesStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Tiers', 'api');
                console.debug('✅ ThirdPartiesStore initialized with', thirdPartiesStore.datas.length, 'third parties');
                notifyRef('Tiers');
            }),
            loadChecked(ctgStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('CTG', 'api');
                console.debug('✅ CTGStore initialized with', ctgStore.listCTG.length, 'CTGs');
                notifyRef('CTG');
            }),
            loadChecked(reservationsStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Réservations', 'api');
                console.debug('✅ ReservationsStore initialized with', reservationsStore.datas.length, 'reservations');
                notifyRef(i18n.global.t('startup.reservations'));
            }),
            loadChecked(reservationsStore.loadCauses()).then(() => {
                end('Causes réservations', 'api');
                console.debug('✅ ReservationCauses initialized with', reservationsStore.causes.length, 'causes');
                notifyRef(i18n.global.t('startup.reservationCauses'));
            }),
            loadChecked(dilutorBathStore.load({ page: 1, pageSize: 0, centerId: centerId })).then(() => {
                end('Bains dilueur', 'api');
                console.debug('✅ DilutorBathStore initialized with', dilutorBathStore.datas.length, 'dilutor baths');
                notifyRef('Bains dilueur');
            }),
            loadChecked(movementTypeStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Types de mouvements', 'api');
                console.debug('✅ MovementTypeStore initialized with', movementTypeStore.datas.length, 'movement types');
                notifyRef('Types de mouvements');
            }),
            loadChecked(typeProtocolStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Types de protocoles', 'api');
                console.debug('✅ TypeProtocolStore initialized with', typeProtocolStore.datas.length, 'type protocols');
                notifyRef('Types de protocoles');
            }),
            loadChecked(samplingEventStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Événements aux prélèvement', 'api');
                console.debug('✅ SamplingEventStore initialized with', samplingEventStore.datas.length, 'sampling events');
                notifyRef(i18n.global.t('startup.samplingEvents'));
            }),
            loadChecked(powderTypeStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Types de poudre', 'api');
                console.debug('✅ PowderTypeStore initialized with', powderTypeStore.datas.length, 'powder types');
                notifyRef('Types de poudre');
            }),

            // Pré-chargement des stores nécessaires à hydrateBoars()
            loadChecked(breedingStudsStore.loadBreedingStud(1, 0)).then(() => {
                end('Élevages', 'api');
                console.debug('✅ BreedingStudsStore initialized with', breedingStudsStore.datas.length, 'breeding studs');
                notifyRef(i18n.global.t('startup.breedingStuds'));
            }),
            loadChecked(psLotStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('PS Lots', 'api');
                console.debug('✅ PSLotStore initialized with', psLotStore.datas.length, 'PS lots');
                notifyRef('PS Lots');
            }),
            loadChecked(bladeStore.load({ page: 1, pageSize: 0, centerId: centerId })).then(() => {
                end('Lames', 'api');
                console.debug('✅ BladeStore initialized with', bladeStore.datas.length, 'blades');
                notifyRef('Lames');
            }),
            loadChecked(codeStockStore.load({ page: 1, pageSize: 0 })).then(() => {
                end('Codes stock', 'api');
                console.debug('✅ CodeStockStore initialized with', codeStockStore.datas.length, 'code stocks');
                notifyRef('Codes stock');
            }),
        ]);
        end('Phase 3 — Référentiels parallèles', 'api');
        console.debug(`✅ Phase 3 (device + référentiels parallel) done in ${(performance.now() - tPhase3_0).toFixed(2)} ms`);
    } catch (e) {
        console.error('❌ Phase 3 (référentiels) failed', e);
        setLoadingStepError(3);
        hasErrors = true;
    }
    notifyRefDone();

    // Phase 4 : reconsolidate — dilutorBath + reservations (boars n'a plus besoin de reconsolidation)
    // dilutorBath : powderType peut être arrivé après la première hydration (race)
    // reservations : boarsStore peut être arrivé après la première hydration (race Phase 3)
    setLoadingStep(4);
    const tRecons0 = performance.now();
    start('Phase 4 — Reconsolidation');
    try {
        const dilutorHydrated = await dilutorBathStore.hydrateDilutorBaths(dilutorBathStore.datas as any);
        dilutorBathStore.datas = dilutorHydrated;

        // Re-hydrater les réserves APRÈS boarsStore consolidé (les réserves utilisent boarsMap)
        reservationsStore.datas = await reservationsStore.hydrateReservations(reservationsStore.datas as any);

        end('Phase 4 — Reconsolidation', 'mapping');
        console.debug(`✅ Reconsolidation done in ${(performance.now() - tRecons0).toFixed(2)} ms (no network)`);
    } catch (e) {
        console.error('❌ Phase 4 (reconsolidation) failed', e);
        setLoadingStepError(4);
        hasErrors = true;
    }

    // Phase 6 : initialiser le filtre center sur les stores lazy-loaded
    // Ensures animals/boars filter is set for future loads triggered by navigation (Animal.vue, etc.)
    boarsStore.filterAnimaux.idCenter = centerId;
    const animalsStore = useAnimals();
    animalsStore.filterAnimaux.idCenter = centerId;
    console.debug('✅ Animals/Boars filter initialized with centerId', centerId);

    startupStore.currentInitStatus = InitializationStatus.DONE;
    console.debug(`🎉 Stores initialization done in ${(performance.now() - tGlobal0).toFixed(2)} ms`);
    console.groupEnd();
    markComplete();
    return { hasErrors };
}

/**
 * Indique qu'un changement de centre est en cours (stores en rechargement).
 * Réactif (ref Vue) : les vues peuvent le watcher pour réagir à la fin de la transition.
 */
export const isSwitchingCenter = ref(false);


/**
 * Recharge tous les stores dépendants du centre lors d'un changement de centre.
 * Reproduit Phase 3 (stores centre-dépendants) + Phase 4 (reconsolidation) de initializeCommonStores.
 */
export async function reloadCenterDependentStores(centerId: number) {
    console.group("🔄 Center change – reloading stores for centerId", centerId);
    const t0 = performance.now();

    const boarsStore = useBoars();
    const buildingStore = useBuildingStore();
    const agentStore = useAgentStore();
    const dilutorBathStore = useDilutorBathStore();
    const bladeStore = useBladeStore();
    const codeStockStore = useCodeStockStore();

    // Rechargement parallèle des stores centre-dépendants
    // Reform/karyotype sont inlinés dans GET /Boar, pas de chargement séparé
    await Promise.all([
        boarsStore.load({ page: 1, pageSize: 0, idCenter: centerId, isPresent: true }),
        buildingStore.load({ page: 1, pageSize: 0, centerId: centerId }),
        agentStore.load({ page: 1, pageSize: 0, centerId: centerId }),
        dilutorBathStore.load({ page: 1, pageSize: 0, centerId: centerId }),
        bladeStore.load({ page: 1, pageSize: 0, centerId: centerId }),
        codeStockStore.load({ page: 1, pageSize: 0 }),
    ]);

    // Reconsolidation (dilutorBath seulement, boars n'en a plus besoin)
    const dilutorHydrated = await dilutorBathStore.hydrateDilutorBaths(dilutorBathStore.datas as any);
    dilutorBathStore.datas = dilutorHydrated;

    // Recharger les réserves du nouveau centre via API (load() appelle hydrateReservations en interne)
    const reservationsStore = useReservations();
    await reservationsStore.load({ page: 1, pageSize: 0, centerId });

    // Mise à jour des filtres centre
    boarsStore.filterAnimaux.idCenter = centerId;
    const animalsStore = useAnimals();
    animalsStore.filterAnimaux.idCenter = centerId;

    isSwitchingCenter.value = false;
    console.debug(`✅ Center stores reloaded in ${(performance.now() - t0).toFixed(2)} ms`);
    console.groupEnd();
}

export async function initializeDeviceStores() {
    // Specific devices
    const pumpStore = usePumpStore();
    pumpStore.init();
    console.debug('PumpStore initialized with', pumpStore.availablePumps.length, 'available pump(s) (id', pumpStore.selectedPumpId, 'selected,', 
        pumpStore.selectedPumpProperties.length, 'properties loaded)'
    );

    const balanceStore = useBalanceStore();
    balanceStore.init();
    console.debug('BalanceStore initialized with', balanceStore.availableBalances.length, 'available balance(s) (id', balanceStore.selectedBalanceId, 'selected,', 
        balanceStore.selectedBalanceProperties.length, 'properties loaded)'
    );

    const labelStore = useLabelStore();
    labelStore.init();
    console.debug('LabelStore initialized with', labelStore.availablePrinters.length, 'available printer(s) (id', labelStore.selectedPrinterId, 'selected,', 
        labelStore.selectedPrinterProperties.length, 'properties loaded)'
    );

    const agpStore = useAGPStore();
    agpStore.init();
    console.debug('AGPStore initialized with', agpStore.availableAGPs.length, 'available AGP(s) (id', agpStore.selectedAGPId, 'selected,', 
        agpStore.selectedAGPProperties.length, 'properties loaded)'
    );

    const pm43cStore = usePM43CStore();
    pm43cStore.init();
    console.debug('PM43CStore initialized with', pm43cStore.availablePM43Cs.length, 'available PM43C(s) (id', pm43cStore.selectedPM43CId, 'selected,', 
        pm43cStore.selectedPM43CProperties.length, 'properties loaded)'
    );

    const rqStore = useRQStore();
    rqStore.init();
    console.debug('RQStore initialized with', rqStore.availableRQs.length, 'available RQ(s) (id', rqStore.selectedRQId, 'selected,',
        rqStore.selectedRQProperties.length, 'properties loaded)'
    );

    const casaStore = useCasaStore();
    casaStore.init();
    console.debug('CasaStore initialized with', casaStore.availableCasas.length, 'available CASA(s) (id', casaStore.selectedCasaId, 'selected,',
        casaStore.selectedCasaProperties.length, 'properties loaded)'
    );
}